import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import Badge from '../../components/common/Badge';
import { useUIStore } from '../../stores/uiStore';
import * as contractTemplatesApi from '../../api/contractTemplates';

/**
 * Template Management Page
 * List and manage contract templates
 */
export default function TemplateManagement() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useUIStore();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await contractTemplatesApi.getTemplates();
      setTemplates(data || []);
    } catch (error) {
      showToast('Failed to load templates', 'error');
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetActive = async (id) => {
    try {
      await contractTemplatesApi.setActiveTemplate(id);
      showToast('Template activated successfully', 'success');
      loadTemplates();
    } catch (error) {
      showToast('Failed to activate template', 'error');
      console.error('Error activating template:', error);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await contractTemplatesApi.deactivateTemplate(id);
      showToast('Template deactivated successfully', 'success');
      loadTemplates();
    } catch (error) {
      showToast('Failed to deactivate template', 'error');
      console.error('Error deactivating template:', error);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const newTemplate = await contractTemplatesApi.duplicateTemplate(id);
      showToast('Template duplicated successfully', 'success');
      loadTemplates();
    } catch (error) {
      showToast('Failed to duplicate template', 'error');
      console.error('Error duplicating template:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await contractTemplatesApi.deleteTemplate(id);
      showToast('Template deleted successfully', 'success');
      loadTemplates();
    } catch (error) {
      showToast('Failed to delete template', 'error');
      console.error('Error deleting template:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contract Templates</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage contract templates for generating contracts
          </p>
        </div>
        <Button onClick={() => navigate('/templates/new')}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Templates List */}
      {templates.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">No templates</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first contract template
            </p>
            <div className="mt-6">
              <Button onClick={() => navigate('/templates/new')}>
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Template
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow">
              {/* Template Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {template.name}
                    </h3>
                    {template.is_active && (
                      <Badge variant="success">Active</Badge>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-600">{template.description}</p>
                  )}
                </div>
              </div>

              {/* Template Info */}
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Variables:</span>
                  <span className="font-medium text-gray-900">
                    {template.template_variables?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Signature Zones:</span>
                  <span className="font-medium text-gray-900">
                    {template.signature_zones?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Updated:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(template.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/templates/${template.id}/edit`)}
                  className="flex-1"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/templates/${template.id}/signature-zones`)}
                  className="flex-1"
                  title="Configure signature zones"
                >
                  <PencilSquareIcon className="h-4 w-4 mr-1" />
                  Signatures
                </Button>
                
                {!template.is_active ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleSetActive(template.id)}
                  >
                    Set Active
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleDeactivate(template.id)}
                  >
                    Deactivate
                  </Button>
                )}
                
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleDuplicate(template.id)}
                  title="Duplicate template"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                </Button>
                
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDelete(template.id)}
                  title="Delete template"
                  disabled={template.is_active}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="text-sm text-blue-800">
          <h4 className="font-semibold mb-2">About Contract Templates:</h4>
          <ul className="list-disc list-inside space-y-1">
            <li>Templates define the structure and content of your contracts</li>
            <li>Use variables (e.g., {'{trainer_name}'}) to create dynamic content</li>
            <li>Define signature zones to specify where signatures should appear</li>
            <li>Only one template can be active at a time for contract generation</li>
            <li>Duplicate existing templates to create variations quickly</li>
          </ul>
        </div>
      </Card>
    </div>
  );
}